import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { isUndefined, isEmpty, filter, sortBy, isEqual, mapValues } from 'lodash'
import {
  Button, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, Skeleton
} from '@ideon-api/ideon-ui'
import Component from '../../common/Component'
import PlanComponent, { INFORMATION_TAB, RATES_TAB, tabsFor, COLUMN_CLASS, ID_COLUMN_CLASS } from './PlanComponent'
import Tabs from '../../common/TabsComponent'
import Panel from '../../common/PanelComponent'
import LimitingFactorsComponent from './LimitingFactorsComponent'

// Fixed keys rather than an index map -- the rows never reorder, and eslint's
// react/no-array-index-key fires on the obvious alternative.
const SKELETON_ROW_KEYS = ['a', 'b', 'c', 'd', 'e']
const HEADER_ROW_CLASS = [
  'flex items-center gap-4 text-sm font-semibold pb-2',
  'border-b border-ideon-gray-40 dark:border-ideon-gray-80'
].join(' ')

export class PlansComponent extends Component {
  static propTypes = {
    plans: PropTypes.array,
    census: PropTypes.object,
    plansByFamily: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.array
    ]),
    needFamilies: PropTypes.bool,
    requestInProgress: PropTypes.bool,
    resourceErrors: PropTypes.string,
    generalError: PropTypes.string,
    audience: PropTypes.string,
    displayMonthlyPremium: PropTypes.bool,
    displayContractCode: PropTypes.bool,
    displayBenefitsTab: PropTypes.bool,
    usePlanRateComponent: PropTypes.bool,
    sortPlansByQuoteRate: PropTypes.bool,
    quoteRates: PropTypes.array
  }

  static renderedProps = [
    'plans',
    'census',
    'plansByFamily',
    'generalError',
    'resourceErrors',
    'requestInProgress',
    // Read in render. The base Component compares ONLY this list, so leaving it
    // out made the skeleton-to-list transition depend on plansByFamily happening
    // to change in the same action -- true today, silently stuck if that changes.
    'needFamilies'
  ]

  constructor(props) {
    super(props)

    // PD-30615 MR3: lifted so "Expand all -> <tab>" can open every card and
    // switch every card's active tab from one control.
    this.state = { openById: {}, activeTabById: {} }
  }

  // The base Component's shouldComponentUpdate (app/frontend/src/components/
  // common/Component.js) only compares `renderedProps` -- it never looks at
  // state -- so without this override, openById/activeTabById changes would
  // never trigger a re-render.
  shouldComponentUpdate(nextProps, nextState) {
    if (!isEqual(nextState, this.state)) return true
    return super.shouldComponentUpdate(nextProps, nextState)
  }

  get plans() {
    const { sortPlansByQuoteRate, audience } = this.props
    if (!sortPlansByQuoteRate && audience === 'Individual') { return sortBy(this.props.plans, ['name'], ['asc']) }

    const plans = filter(this.props.plans, (plan) => {
      return this.props.quoteRates.find((rate) => rate.plan_id === plan.id)
    })

    return sortBy(plans, ['name'], ['asc'])
  }

  get searchPartiallyFailed() {
    return !isUndefined(this.props.generalError) || !isUndefined(this.props.resourceErrors)
  }

  get plansComponents() {
    const { plans, plansByFamily } = this.props

    if (isEmpty(plans) || (plansByFamily && isEmpty(plansByFamily))) {
      return (
        <div className="ideon-ui-scope">
          <div className="text-sm font-medium py-2">No plans found</div>
        </div>
      )
    }

    return (
      <div className="ideon-ui-scope">
        {this.searchPartiallyFailed && this.partialFailureNotice()}
        {this.renderToolbar()}

        <div className={HEADER_ROW_CLASS}>
          <div className="flex-1">Plan Name</div>
          <div className={ID_COLUMN_CLASS}>ID</div>
          {this.props.displayMonthlyPremium && <div className={COLUMN_CLASS}>Monthly Premium</div>}
          {this.props.displayContractCode && <div className={COLUMN_CLASS}>Contract Codes</div>}
        </div>

        {this.planComponents}
      </div>
    )
  }

  get planComponents() {
    return this.plans.map((plan) => (
      <PlanComponent
        plan={plan}
        key={`${plan.id}`}
        census={this.props.census}
        plansByFamily={this.props.plansByFamily}
        displayMonthlyPremium={this.props.displayMonthlyPremium}
        displayContractCode={this.props.displayContractCode}
        displayBenefitsTab={this.props.displayBenefitsTab}
        usePlanRateComponent={this.props.usePlanRateComponent}
        open={this.isOpen(plan.id)}
        onOpenChange={(open) => this.setOpen(plan.id, open)}
        tab={this.tabFor(plan.id)}
        onTabChange={(tab) => this.setTab(plan.id, tab)}
      />
    ))
  }

  // Rates is offered only for Individual. Everywhere else the rates panel
  // fetches per plan into a single shared Redux slot -- every card mounting at
  // once resets and overwrites the last, so one plan loads and the rest sit on
  // "Loading rates" forever. Individual never fetches (PlanComponent's
  // censusFamilyPlansPremiums only populates rate_id from plansByFamily), so
  // it is safe. Keying that slice by plan_id is the real fix and is its own
  // ticket; until then this menu does not offer an action that cannot work.
  get expandAllTabs() {
    const tabs = tabsFor(this.props.displayBenefitsTab)
    if (this.props.audience === 'Individual') { return tabs }

    return tabs.filter((tab) => tab !== RATES_TAB)
  }

  isOpen = (planId) => !!this.state.openById[planId]

  tabFor = (planId) => this.state.activeTabById[planId] || INFORMATION_TAB

  setOpen = (planId, open) => {
    this.setState((prev) => ({ openById: { ...prev.openById, [planId]: open } }))
  }

  setTab = (planId, tab) => {
    this.setState((prev) => ({ activeTabById: { ...prev.activeTabById, [planId]: tab } }))
  }

  expandAllToTab = (tab) => {
    const openById = {}
    const activeTabById = {}
    this.plans.forEach((plan) => {
      openById[plan.id] = true
      activeTabById[plan.id] = tab
    })
    this.setState({ openById, activeTabById })
  }

  collapseAll = () => {
    this.setState((prev) => ({ openById: mapValues(prev.openById, () => false) }))
  }

  renderToolbar = () => (
    <div className="flex items-center gap-2 mb-2">
      <DropdownMenu>
        <DropdownMenuTrigger>Expand all</DropdownMenuTrigger>
        <DropdownMenuContent>
          {this.expandAllTabs.map((tab) => (
            <DropdownMenuItem key={tab} onSelect={() => this.expandAllToTab(tab)}>{tab}</DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Button type="button" variant="outline" onClick={this.collapseAll}>Collapse all</Button>
    </div>
  )

  // Deliberately does NOT repeat the error text (PD-30615). apiErrorMiddleware
  // raises a banner carrying the message for every failed request, so printing
  // it again here put the same sentence on screen twice -- three times before
  // the FlashMessages reducer started collapsing duplicates. The banner is
  // dismissible and dismissing it does not clear the slice, so this does not
  // tell the reader to go and look at it either.
  searchFailedComponent = () => (
    <div className="ideon-ui-scope">
      <div className="py-3 text-sm text-ideon-gray-70 dark:text-ideon-gray-60">
        The plan search did not complete, so there are no plans to show.
      </div>
    </div>
  )

  // Shown above the plans that DID load when part of the search failed.
  partialFailureNotice = () => (
    <div
      className={[
        'mb-3 px-3 py-2 text-sm rounded border',
        'border-ideon-gray-40 text-ideon-gray-70',
        'dark:border-ideon-gray-80 dark:text-ideon-gray-60'
      ].join(' ')}
    >
      Part of the plan search failed. The plans below are the ones that loaded, and the list is incomplete.
    </div>
  )

  // A plan search hits vericred and routinely takes seconds. A one-line
  // "Loading plans" gave no sense of that, and the page height jumped when the
  // rows arrived (PD-30615). This mirrors the real list -- the same header row,
  // then placeholder rows at the collapsed Expander's height -- so the layout is
  // already the right shape before the data lands.
  loadingSkeleton = () => (
    <div className="ideon-ui-scope" aria-busy="true">
      <span className="sr-only">Loading plans</span>

      <div className={HEADER_ROW_CLASS} aria-hidden="true">
        <div className="flex-1">Plan Name</div>
        <div className={ID_COLUMN_CLASS}>ID</div>
        {this.props.displayMonthlyPremium && <div className={COLUMN_CLASS}>Monthly Premium</div>}
        {this.props.displayContractCode && <div className={COLUMN_CLASS}>Contract Codes</div>}
      </div>

      {SKELETON_ROW_KEYS.map((key) => (
        <div
          key={key}
          aria-hidden="true"
          className="flex items-center gap-4 py-3 border-b border-ideon-gray-30 dark:border-ideon-gray-80"
        >
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 w-40" />
          {this.props.displayMonthlyPremium && <Skeleton className="h-4 w-28" />}
          {this.props.displayContractCode && <Skeleton className="h-4 w-28" />}
        </div>
      ))}
    </div>
  )

  render() {
    const {
      plans,
      requestInProgress,
      census,
      generalError,
      resourceErrors,
      needFamilies,
      audience,
      plansByFamily
    } = this.props

    const failed = !isUndefined(generalError) || !isUndefined(resourceErrors)

    // Degrade, do not erase. getCensusPlanSearches fires one request per family
    // and each resolves independently, so one family failing leaves every other
    // family's plans sitting in the store, already fetched and correct. The old
    // code -- and my first pass at this -- replaced the whole list with an error,
    // throwing those away and telling the user there were no plans when there
    // were. Now a failure only costs the part that failed, and the gap is
    // disclosed above what did load (~/.claude/rules/partial-failure-degrades).
    if (failed && isEmpty(plans)) {
      return this.searchFailedComponent()
    }

    // Once something has failed, the outstanding requests are not coming back.
    // Falling into a loading branch here would animate the skeleton forever.
    if (!failed && requestInProgress) {
      return this.loadingSkeleton()
    }

    if (!plans || !census) {
      return failed ? this.searchFailedComponent() : null
    }

    if (!failed && audience === 'Individual' && needFamilies) {
      return this.loadingSkeleton()
    }

    if (!failed && audience !== 'Individual' && !plansByFamily) {
      return this.loadingSkeleton()
    }

    if (audience !== 'Small Group') { return this.plansComponents }

    return (
      <Tabs selected={0}>
        <Panel label="Quoted Plans">{this.plansComponents}</Panel>
        <Panel label="Limiting Factors">
          <LimitingFactorsComponent />
        </Panel>
      </Tabs>
    )
  }
}

const mapStateToProps = (state) => {
  return {
    quoteRates: state.quoteRates.create.responseAttributes
  }
}

export default connect(mapStateToProps, null)(PlansComponent)
